import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment, WsmFitmentValueKind } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Input, Select, Text } from "@saleor/macaw-ui-next";
import React, { ReactNode, useState } from "react";
import { useIntl } from "react-intl";

import {
  getWsmFitmentErrorMessage,
  getWsmFitmentFieldError,
  getWsmFitmentGeneralErrors,
} from "../errors";
import { axisMessages, schemaMessages } from "../messages";
import { WsmFitmentAxisFormData } from "../types";
import { valueKindChoices } from "../utils";

const FIELDS = ["name", "displayLabel", "valueKind"];

/**
 * The editor an axis and a qualifier share: a name, a display label, and for an
 * axis how its values sort. The values card is passed in, because it is what
 * differs between the two (which connection it pages, which mutations it calls).
 */
export interface WsmFitmentNamedDetailsPageProps {
  title: string;
  backHref: string;
  /** Present on an axis; a qualifier has no value kind. */
  withValueKind: boolean;
  initialData: WsmFitmentAxisFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmFitmentAxisFormData) => void;
  onDelete?: () => void;
  children?: ReactNode;
}

export const WsmFitmentNamedDetailsPage = ({
  title,
  backHref,
  withValueKind,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
  children,
}: WsmFitmentNamedDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmFitmentAxisFormData>(initialData);
  const change = (patch: Partial<WsmFitmentAxisFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const fieldError = (field: string) => getWsmFitmentFieldError(errors, field);
  const helper = (field: string, fallback?: string) =>
    getWsmFitmentErrorMessage(fieldError(field), intl) ?? fallback;
  const generalErrors = getWsmFitmentGeneralErrors(errors, FIELDS);

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav href={backHref} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-fitment-named-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(schemaMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="grid" __gridTemplateColumns="1fr 1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(schemaMessages.name)}
                data-test-id="wsm-fitment-named-name"
                disabled={disabled}
                value={data.name}
                onChange={event => change({ name: event.target.value })}
                error={!!fieldError("name")}
                helperText={helper("name")}
              />
              <Input
                label={intl.formatMessage(schemaMessages.displayLabel)}
                data-test-id="wsm-fitment-named-display-label"
                disabled={disabled}
                value={data.displayLabel}
                onChange={event => change({ displayLabel: event.target.value })}
                error={!!fieldError("displayLabel")}
                helperText={helper(
                  "displayLabel",
                  intl.formatMessage(schemaMessages.displayLabelHelp),
                )}
              />
              {withValueKind && (
                <Select
                  label={intl.formatMessage(axisMessages.valueKind)}
                  data-test-id="wsm-fitment-axis-value-kind"
                  disabled={disabled}
                  options={valueKindChoices({
                    text: intl.formatMessage(axisMessages.valueKindText),
                    number: intl.formatMessage(axisMessages.valueKindNumber),
                  })}
                  value={data.valueKind}
                  onChange={value => change({ valueKind: value as WsmFitmentValueKind })}
                  error={!!fieldError("valueKind")}
                  helperText={helper("valueKind", intl.formatMessage(axisMessages.valueKindHelp))}
                />
              )}
            </Box>
            {generalErrors.map((error, index) => (
              <Text
                key={index}
                size={2}
                color="critical1"
                display="block"
                marginTop={2}
                data-test-id="wsm-fitment-named-error"
              >
                {getWsmFitmentErrorMessage(error, intl)}
              </Text>
            ))}
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        {children}
      </DetailPageLayout.Content>
      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(backHref)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-fitment-named-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmFitmentNamedDetailsPage;
